/* global window */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { UniversalContext, RENDER_PHASE } from '../../../_universal';
import { WIDGET_CHROME, WIDGET_SCHEDULING } from '../../constants';
import { locationShape } from '../../shapes';
import { trackAffiliatePage } from '../../utils/affiliateTracking';
import { savePrefillToken } from '../../utils/session/prefillToken';
import { encodeData, decodeData } from '../../utils/session/utils';
import { getQueryFromLocation } from '../../utils/urls';
import LegacyWidgetSettingsPartnerData from './LegacyWidgetSettingsPartnerData';
import WidgetSettingsContext from './WidgetSettingsContext';
import PartnerDataQuery from './PartnerDataQuery';

const DEFAULT_SESSION_NAME = 'lm_widget_settings';

function booleanField(options) {
  return {
    ...options,
    toValue: value => value === 'true',
  };
}

function stringField(options) {
  return {
    ...options,
    toValue: value => (value === '' ? null : value),
  };
}

function arrayField(options) {
  return {
    ...options,
    toValue: value => (value.length < 1 ? [] : value),
  };
}

function choiceField({ choices, ...options }) {
  return {
    ...options,
    toValue: value => (choices.indexOf(value) === -1 ? null : value),
  };
}

function processForm(schema, input) {
  return Object.keys(schema).reduce((output, fieldName) => {
    const fieldOptions = schema[fieldName];
    const fromKeys = fieldOptions.fromKeys || [fieldName];
    fromKeys.forEach(fromKey => {
      if (fromKey in input) {
        output[fieldName] = fieldOptions.toValue(input[fromKey]);
      }
    });
    return output;
  }, {});
}

const formSchema = {
  chrome: choiceField({ choices: Object.values(WIDGET_CHROME) }),
  scheduling: choiceField({ choices: Object.values(WIDGET_SCHEDULING) }),
  partnerId: stringField({ fromKeys: ['organization_id', 'partnerId'] }),
  hidePartnerLogo: booleanField(),
  aftId: stringField(),
  aftType: stringField(),
  aft: arrayField(),
};

/**
 * This component uses react-cookie to get/set the widget settings from the session/query params.
 */
class WidgetSettings extends Component {
  static propTypes = {
    children: PropTypes.node.isRequired,
    location: locationShape,
  };

  static contextType = UniversalContext;

  constructor(props, context) {
    super(props, context);
    this.updateWidgetSettings();
    this.savePrefillToken();
    this.trackAffiliatePage();
  }

  updateWidgetSettings() {
    const { location } = this.props;
    const query = getQueryFromLocation(location);
    this.widgetSettings = {
      ...decodeData(
        typeof window !== 'undefined' ? window.sessionStorage.getItem(DEFAULT_SESSION_NAME) : null
      ),
      ...processForm(formSchema, query),
    };

    // If the new `aft` param is present, make sure to remove
    // the old params to avoid any conflicts
    if (this.widgetSettings.aft != null) {
      delete this.widgetSettings.aftId;
      delete this.widgetSettings.aftType;
    }

    if (this.context.renderPhase !== RENDER_PHASE.BEFORE_SERVER_RENDER) {
      if (typeof window !== 'undefined')
        window.sessionStorage.setItem(DEFAULT_SESSION_NAME, encodeData(this.widgetSettings));
    }
  }

  savePrefillToken() {
    if (this.context.renderPhase === RENDER_PHASE.BEFORE_SERVER_RENDER) return;

    const { location } = this.props;
    const query = getQueryFromLocation(location);
    if (typeof query.prefillToken !== 'undefined') {
      savePrefillToken(query.prefillToken);
    }
  }

  trackAffiliatePage() {
    trackAffiliatePage({
      aftId: this.widgetSettings.aftId,
      aftType: this.widgetSettings.aftType,
      aft: this.widgetSettings.aft,
    });
  }

  render() {
    return (
      <PartnerDataQuery widgetSettings={this.widgetSettings}>
        {({ widgetSettingsData }) => (
          <WidgetSettingsContext.Provider value={widgetSettingsData}>
            <LegacyWidgetSettingsPartnerData widgetSettings={widgetSettingsData}>
              {this.props.children}
            </LegacyWidgetSettingsPartnerData>
          </WidgetSettingsContext.Provider>
        )}
      </PartnerDataQuery>
    );
  }
}

export default WidgetSettings;
